import { useCallback, useEffect, useRef } from 'react';
import { FieldGeneral } from 'framework/field/types';

export const createTextMeasurer = () => {
  const span = document.createElement('span');
  Object.assign(span.style, {
    position: 'absolute',
    visibility: 'hidden',
    left: '-9999px',
    top: '-9999px',
    boxSizing: 'border-box',
    border: 'none',
    margin: '0',
    // font properties
    fontFamily: "'Roboto', sans-serif",
    fontSize: '13px',
    fontWeight: '400',
  });

  document.body.appendChild(span);

  const measure = (text: string, theme: Pick<FieldGeneral, 'font_family' | 'font_size'>) => {
    Object.assign(span.style, {
      fontFamily: theme.font_family,
      fontSize: theme.font_size,
      fontWeight: 400,
    });
    span.textContent = text || '';

    return Math.ceil(span.getBoundingClientRect().width);
  };

  measure.cleanup = () => {
    span.remove();
  };

  return measure;
};

export function useMeasureText(theme: FieldGeneral) {
  const measurerRef = useRef<ReturnType<typeof createTextMeasurer> | null>(null);

  if (measurerRef.current === null) {
    measurerRef.current = createTextMeasurer();
  }

  useEffect(() => {
    return () => {
      measurerRef.current?.cleanup();
      measurerRef.current = null;
    };
  }, []);

  const measure = useCallback(
    (text: string) => {
      if (!measurerRef.current) return 0;

      return measurerRef.current(text, {
        font_family: theme.font_family,
        font_size: theme.font_size,
      });
    },
    [theme]
  );

  return measure;
}
